import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Opportunity,
  UserProfile,
  BidOutcome,
  DebriefFactor,
  PostMortemDebrief,
  FoiaDebriefStatus,
  ClientDebriefDetails,
  RebidTracking,
} from "../../types";
import { recordBidOutcome } from "../../services/opportunitiesService";
import { calculateRebidDates } from "../../services/rebidService";
import {
  Trophy,
  X,
  AlertCircle,
  CheckCircle2,
  DollarSign,
  Calendar,
  Sparkles,
  Loader2,
  Building2,
  HelpCircle,
  FileSpreadsheet,
  Clock,
} from "lucide-react";

export interface PostMortemModalProps {
  opportunity: Opportunity;
  user: UserProfile | null;
  initialOutcome?: BidOutcome;
  isOpen: boolean;
  onClose: () => void;
  onOutcomeSaved?: (outcome: BidOutcome) => void;
  onOpenHarvest?: () => void;
}

const DEBRIEF_FACTORS: { value: DebriefFactor; label: string }[] = [
  { value: "wbe_certification", label: "WBE Certification" },
  { value: "technical_solution", label: "Technical Solution" },
  { value: "pricing", label: "Pricing / Cost" },
  { value: "past_performance", label: "Past Performance" },
  { value: "incumbent_advantage", label: "Incumbent Advantage" },
  { value: "compliance_gap", label: "Compliance Gap" },
  { value: "turnaround", label: "Turnaround & Delivery" },
  { value: "timeline", label: "Timeline Feasibility" },
  { value: "relationship", label: "Client Relationship" },
  { value: "other", label: "Other" },
];

export const PostMortemModal: React.FC<PostMortemModalProps> = ({
  opportunity,
  user,
  initialOutcome = "won",
  isOpen,
  onClose,
  onOutcomeSaved,
  onOpenHarvest,
}) => {
  const existingDebrief = opportunity.postMortem;
  const existingTracking = opportunity.rebidTracking || existingDebrief?.rebidTracking;

  const [outcome, setOutcome] = useState<BidOutcome>(opportunity.outcome || initialOutcome);
  const [decidedAt, setDecidedAt] = useState<string>(
    existingDebrief?.decidedAt || new Date().toISOString().split("T")[0] || "",
  );
  const [finalContractValue, setFinalContractValue] = useState<string>(
    existingDebrief?.finalContractValue != null ? String(existingDebrief.finalContractValue) : "",
  );
  const [clientDebriefReceived, setClientDebriefReceived] = useState<boolean>(
    existingDebrief?.clientDebriefReceived || false,
  );
  const [clientFeedbackNotes, setClientFeedbackNotes] = useState<string>(
    existingDebrief?.clientFeedbackNotes || "",
  );
  const [factors, setFactors] = useState<DebriefFactor[]>(existingDebrief?.factors || []);
  const [winningCompetitor, setWinningCompetitor] = useState<string>(
    existingDebrief?.winningCompetitor || "",
  );
  const [lessonsLearned, setLessonsLearned] = useState<string>(
    existingDebrief?.lessonsLearned || "",
  );

  // FOIA / Client Debrief specifics
  const [foiaStatus, setFoiaStatus] = useState<FoiaDebriefStatus>(
    existingDebrief?.clientDebriefDetails?.status ||
      (existingDebrief?.clientDebriefReceived ? "received" : "not_requested"),
  );
  const [ourScore, setOurScore] = useState<string>(
    existingDebrief?.clientDebriefDetails?.ourScore != null
      ? String(existingDebrief.clientDebriefDetails.ourScore)
      : "",
  );
  const [winningScore, setWinningScore] = useState<string>(
    existingDebrief?.clientDebriefDetails?.winningScore != null
      ? String(existingDebrief.clientDebriefDetails.winningScore)
      : "",
  );
  const [totalPossibleScore, setTotalPossibleScore] = useState<string>(
    existingDebrief?.clientDebriefDetails?.totalPossibleScore != null
      ? String(existingDebrief.clientDebriefDetails.totalPossibleScore)
      : "",
  );

  // Rebid Tracking
  const [trackRebid, setTrackRebid] = useState<boolean>(
    existingTracking?.isRebidTracked ?? outcome === "won",
  );
  const [contractStartDate, setContractStartDate] = useState<string>(
    existingTracking?.contractStartDate || decidedAt,
  );
  const [baseYears, setBaseYears] = useState<number>(2);
  const [optionYears, setOptionYears] = useState<number>(existingTracking?.optionYears ?? 1);

  const calculatedRebid = useMemo(() => {
    if (!trackRebid || !contractStartDate) return null;
    try {
      return calculateRebidDates({
        contractStartDate,
        baseYears,
        optionYears,
      });
    } catch {
      return null;
    }
  }, [trackRebid, contractStartDate, baseYears, optionYears]);

  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const toggleFactor = (f: DebriefFactor) => {
    setFactors((prev) => (prev.includes(f) ? prev.filter((item) => item !== f) : [...prev, f]));
  };

  const handleSave = async (openHarvestAfter = false) => {
    setIsSaving(true);
    setErrorMessage(null);

    try {
      const parsedValue = finalContractValue.trim()
        ? parseFloat(finalContractValue.replace(/[^0-9.]/g, ""))
        : null;

      const clientDebriefDetails: ClientDebriefDetails | undefined =
        clientDebriefReceived || foiaStatus !== "not_requested"
          ? {
              status: foiaStatus,
              requestedAt: existingDebrief?.clientDebriefDetails?.requestedAt,
              scheduledDate: existingDebrief?.clientDebriefDetails?.scheduledDate,
              meetingNotes: clientFeedbackNotes.trim() || undefined,
              ourScore: ourScore.trim() ? parseFloat(ourScore) : undefined,
              winningScore: winningScore.trim() ? parseFloat(winningScore) : undefined,
              totalPossibleScore: totalPossibleScore.trim()
                ? parseFloat(totalPossibleScore)
                : undefined,
            }
          : undefined;

      const rebidTracking: RebidTracking | undefined =
        trackRebid && calculatedRebid
          ? {
              isRebidTracked: true,
              contractStartDate,
              contractEndDate: calculatedRebid.contractEndDate,
              optionYears,
              maxExpirationDate: calculatedRebid.maxExpirationDate,
              rebidCaptureNoticeDate: calculatedRebid.rebidCaptureNoticeDate,
              rebidOpportunityId: existingTracking?.rebidOpportunityId,
            }
          : undefined;

      const debriefPayload: Omit<
        PostMortemDebrief,
        "recordedAt" | "recordedBy" | "recordedByEmail"
      > = {
        decidedAt: decidedAt || new Date().toISOString().split("T")[0] || "",
        finalContractValue: isNaN(Number(parsedValue)) ? null : parsedValue,
        clientDebriefReceived: clientDebriefReceived || foiaStatus === "received",
        clientFeedbackNotes: clientFeedbackNotes.trim() || undefined,
        factors,
        winningCompetitor: outcome !== "won" ? winningCompetitor.trim() : undefined,
        lessonsLearned: lessonsLearned.trim() || undefined,
        creditedAnswerIds: existingDebrief?.creditedAnswerIds || [],
        harvestedAnswerIds: existingDebrief?.harvestedAnswerIds || [],
        flaggedWeakAnswerIds: existingDebrief?.flaggedWeakAnswerIds || [],
        clientDebriefDetails,
        rebidTracking,
      };

      await recordBidOutcome(
        opportunity.id,
        outcome,
        debriefPayload,
        user ? { displayName: user.displayName, email: user.email } : undefined,
        opportunity.stageHistory || [],
      );

      if (onOutcomeSaved) {
        onOutcomeSaved(outcome);
      }

      onClose();

      if (openHarvestAfter && onOpenHarvest) {
        onOpenHarvest();
      }
    } catch (err) {
      console.error("Failed to record bid outcome:", err);
      setErrorMessage(err instanceof Error ? err.message : "Failed to record bid outcome.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs"
        />

        {/* Modal Window */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative z-10 w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div
                className={`rounded-xl p-2 ${
                  outcome === "won"
                    ? "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"
                    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                }`}
              >
                {outcome === "won" ? (
                  <Trophy className="h-5 w-5" />
                ) : (
                  <Building2 className="h-5 w-5" />
                )}
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Post-Mortem & Bid Outcome
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                  {opportunity.client} — {opportunity.title}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {errorMessage && (
              <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200">
                <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Outcome Radio Buttons */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Bid Outcome
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <button
                  type="button"
                  onClick={() => setOutcome("won")}
                  className={`btn-animate flex items-center justify-center gap-2 rounded-xl border p-2.5 text-xs font-bold transition-all ${
                    outcome === "won"
                      ? "border-emerald-500 bg-emerald-50 text-emerald-800 shadow-2xs dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400"
                  }`}
                >
                  <Trophy className="h-3.5 w-3.5 text-amber-500" />
                  <span>Won</span>
                </button>

                <button
                  type="button"
                  onClick={() => setOutcome("lost")}
                  className={`btn-animate flex items-center justify-center gap-2 rounded-xl border p-2.5 text-xs font-bold transition-all ${
                    outcome === "lost"
                      ? "border-rose-500 bg-rose-50 text-rose-800 shadow-2xs dark:border-rose-700 dark:bg-rose-950/50 dark:text-rose-200"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400"
                  }`}
                >
                  <span>Lost</span>
                </button>

                <button
                  type="button"
                  onClick={() => setOutcome("disqualified")}
                  className={`btn-animate flex items-center justify-center gap-2 rounded-xl border p-2.5 text-xs font-bold transition-all ${
                    outcome === "disqualified"
                      ? "border-amber-500 bg-amber-50 text-amber-900 shadow-2xs dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400"
                  }`}
                >
                  <span>Disqualified</span>
                </button>

                <button
                  type="button"
                  onClick={() => setOutcome("withdrawn")}
                  className={`btn-animate flex items-center justify-center gap-2 rounded-xl border p-2.5 text-xs font-bold transition-all ${
                    outcome === "withdrawn"
                      ? "border-slate-500 bg-slate-100 text-slate-800 shadow-2xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400"
                  }`}
                >
                  <span>Withdrawn</span>
                </button>
              </div>
            </div>

            {/* Date & Contract Value Grid */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                  <span>Award / Decision Date</span>
                </label>
                <input
                  type="date"
                  value={decidedAt}
                  onChange={(e) => setDecidedAt(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1.5">
                  <DollarSign className="h-3.5 w-3.5 text-slate-400" />
                  <span>Final Contract Value (USD, Optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. 150,000"
                  value={finalContractValue}
                  onChange={(e) => setFinalContractValue(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>
            </div>

            {/* Deciding Factors Multi-select */}
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5 flex items-center justify-between">
                <span>Primary Deciding Factors</span>
                <span className="text-[11px] text-slate-400">Select all that apply</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {DEBRIEF_FACTORS.map((f) => {
                  const active = factors.includes(f.value);
                  return (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => toggleFactor(f.value)}
                      className={`btn-animate inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                        active
                          ? "bg-[#2ba5b5] text-white shadow-2xs"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                      }`}
                    >
                      {active ? (
                        <CheckCircle2 className="h-3 w-3" />
                      ) : (
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                      )}
                      <span>{f.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Winning Competitor (relevant if not won) */}
            {outcome !== "won" && (
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Winning Competitor (If known)
                </label>
                <input
                  type="text"
                  placeholder="e.g. TransPerfect, LanguageLine, Incumbent Agency"
                  value={winningCompetitor}
                  onChange={(e) => setWinningCompetitor(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>
            )}

            {/* Client Debrief & FOIA Scorecards */}
            <div className="space-y-3 rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5 dark:border-slate-800 dark:bg-slate-950/40">
              <div className="flex items-center justify-between">
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-800 dark:text-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={clientDebriefReceived}
                    onChange={(e) => {
                      setClientDebriefReceived(e.target.checked);
                      if (e.target.checked && foiaStatus === "not_requested") {
                        setFoiaStatus("received");
                      }
                    }}
                    className="rounded text-[#2ba5b5] focus:ring-[#2ba5b5]"
                  />
                  <FileSpreadsheet className="h-4 w-4 text-[#2ba5b5]" />
                  <span>Client Debrief Notes Received</span>
                </label>
                <select
                  value={foiaStatus}
                  onChange={(e) => {
                    const st = e.target.value as FoiaDebriefStatus;
                    setFoiaStatus(st);
                    if (st === "received") setClientDebriefReceived(true);
                  }}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800 outline-hidden dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                >
                  <option value="not_requested">Not Requested</option>
                  <option value="requested">Requested / Awaiting</option>
                  <option value="scheduled">Debrief Scheduled</option>
                  <option value="received">Received / Completed</option>
                  <option value="declined">Declined by Agency</option>
                </select>
              </div>

              {(foiaStatus === "received" || clientDebriefReceived) && (
                <div className="space-y-2 pt-1 border-t border-slate-200/60 dark:border-slate-800">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400 mb-0.5">
                        Our Score
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        placeholder="e.g. 92.5"
                        value={ourScore}
                        onChange={(e) => setOurScore(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 outline-hidden dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400 mb-0.5">
                        Winning Score
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        placeholder="e.g. 96.0"
                        value={winningScore}
                        onChange={(e) => setWinningScore(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 outline-hidden dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400 mb-0.5">
                        Total Possible
                      </label>
                      <input
                        type="number"
                        step="1"
                        placeholder="e.g. 100"
                        value={totalPossibleScore}
                        onChange={(e) => setTotalPossibleScore(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 outline-hidden dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400 mb-1">
                      Evaluator Notes & Client Feedback
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Enter verbatim feedback or debrief call notes from the client..."
                      value={clientFeedbackNotes}
                      onChange={(e) => {
                        setClientFeedbackNotes(e.target.value);
                        setClientDebriefReceived(true);
                      }}
                      className="w-full rounded-lg border border-slate-200 bg-white p-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Rebid & Contract Expiry Tracking */}
            <div className="space-y-2.5 rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5 dark:border-slate-800 dark:bg-slate-950/40">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-800 dark:text-slate-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={trackRebid}
                  onChange={(e) => setTrackRebid(e.target.checked)}
                  className="rounded text-[#2ba5b5] focus:ring-[#2ba5b5]"
                />
                <Clock className="h-4 w-4 text-[#2ba5b5]" />
                <span>Track Contract Expiry & Rebid Capture Window</span>
              </label>

              {trackRebid && (
                <div className="space-y-2 pt-1 border-t border-slate-200/60 dark:border-slate-800">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400 mb-0.5">
                        Contract Start Date
                      </label>
                      <input
                        type="date"
                        value={contractStartDate}
                        onChange={(e) => setContractStartDate(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 outline-hidden dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400 mb-0.5">
                        Base Term (Years)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={baseYears}
                        onChange={(e) => setBaseYears(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 outline-hidden dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400 mb-0.5">
                        Option Periods (Years)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="5"
                        value={optionYears}
                        onChange={(e) => setOptionYears(Math.max(0, parseInt(e.target.value) || 0))}
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 outline-hidden dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      />
                    </div>
                  </div>

                  {calculatedRebid && (
                    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-teal-50/70 p-2 text-[11px] text-teal-900 dark:bg-teal-950/40 dark:text-teal-200">
                      <span>
                        Base End: <strong>{calculatedRebid.contractEndDate}</strong>
                      </span>
                      <span>·</span>
                      <span>
                        Max Expiration: <strong>{calculatedRebid.maxExpirationDate}</strong>
                      </span>
                      <span>·</span>
                      <span>
                        Rebid Alert: <strong>{calculatedRebid.rebidCaptureNoticeDate}</strong>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Internal Lessons Learned */}
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1.5">
                <HelpCircle className="h-3.5 w-3.5 text-slate-400" />
                <span>Internal Lessons Learned & Takeaways</span>
              </label>
              <textarea
                rows={3}
                placeholder="What resonated with the committee? What should we improve on future bids of this type?"
                value={lessonsLearned}
                onChange={(e) => setLessonsLearned(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white p-2.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between border-t border-slate-200 bg-slate-50/80 px-6 py-3.5 dark:border-slate-800 dark:bg-slate-900/60">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="btn-animate rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              Cancel
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleSave(false)}
                disabled={isSaving}
                className="btn-animate inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              >
                {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                <span>Save Outcome & Close</span>
              </button>

              {onOpenHarvest && (
                <button
                  type="button"
                  onClick={() => handleSave(true)}
                  disabled={isSaving}
                  className="btn-animate inline-flex items-center gap-1.5 rounded-xl bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-2xs hover:bg-[#238a97]"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Save & Harvest to Library</span>
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
